import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

// Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

// Services
import PassengerApi from "Services/Passenger";

// Components
import PassengerDocuments from "./Passenger_documents";
import PassengerStatus from "./Status";

// Helpers
import {
  PASSENGER_DOCUMENT_DEFINITIONS,
  buildEmptyPassengerDocuments,
  isBlobUrl,
  normalizePassengerDocuments,
  resolveMediaUrl,
} from "./Passenger_documents/documents";

// Data
import countryCodeData from "./data/countryCode/index.json";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const defaultAvatar = "/assets/images/user/avatar.png";

const resolvePassengerDocumentTypes = (payload) => {
  const candidates = [
    payload,
    payload?.data,
    payload?.documents,
    payload?.documentTypes,
    payload?.document_types,
    payload?.items,
    payload?.list,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return PASSENGER_DOCUMENT_DEFINITIONS;
};

const PassengerUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [value, setValue] = useState(0);
  const [skipButton, setSkipButton] = useState(false);
  const [backButton, setBackButton] = useState(false);
  const [details, setDetails] = useState({});
  const [profilePreview, setProfilePreview] = useState(defaultAvatar);
  const [passengerDocumentTypes, setPassengerDocumentTypes] = useState(
    PASSENGER_DOCUMENT_DEFINITIONS
  );
  const [passengerDocumentRecords, setPassengerDocumentRecords] = useState([]);
  const [passengerDocuments, setPassengerDocuments] = useState(
    buildEmptyPassengerDocuments(PASSENGER_DOCUMENT_DEFINITIONS)
  );

  const maxFileSize = 5242880;

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than ${formatBytes(maxFileSize)}`);
      return {
        code: "file-too-large",
        message: `File size is larger than ${formatBytes(maxFileSize)}`,
      };
    }
    return null;
  };

  const basicFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      gender: "",
      country: "",
      mobile: "",
      countryCode: "",
      customField1: "",
      customField2: "",
      passengerProfileImage: "",
      passengerProfileImagefile: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First Name is required").nullable(),
      email: yup
        .string()
        .email("Invalid email format")
        .required("Email is required")
        .nullable(),
      countryCode: yup.string().required("Country Code is required").nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      gender: yup.string().required("Gender is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: (values) => handleUpdatePassengerBasic(values),
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (value === 0) {
      setSkipButton(false);
      setBackButton(false);
    }

    if (value === 1) {
      setSkipButton(true);
      setBackButton(true);
    }

    if (value === 2) {
      setSkipButton(false);
      setBackButton(true);
    }
  }, [value]);

  const handleBackButton = () => {
    setValue((prev) => Math.max(prev - 1, 0));
  };

  const handleSkipButton = () => {
    setValue((prev) => Math.min(prev + 1, 2));
  };

  const handleOpenPassengerDocumentsTab = () => {
    setValue(1);
  };

  const getDocumentTypes = useCallback(() => {
    if (!id) {
      return;
    }

    const onSuccess = (res) => {
      if (res?.status === 200) {
        setPassengerDocumentTypes(resolvePassengerDocumentTypes(res?.data?.data));
        return;
      }

      setPassengerDocumentTypes(PASSENGER_DOCUMENT_DEFINITIONS);
    };

    const onFailure = () => {
      setPassengerDocumentTypes(PASSENGER_DOCUMENT_DEFINITIONS);
    };

    PassengerApi.getDocumentsTypes().then(onSuccess, onFailure);
  }, [id]);

  const getPassengerDocuments = useCallback(() => {
    if (!id) {
      return;
    }

    const onSuccess = (res) => {
      const rawData = res?.data?.data;
      const documentRecords = Array.isArray(rawData?.driverDocuments)
        ? rawData.driverDocuments
        : Array.isArray(rawData?.documents)
        ? rawData.documents
        : Array.isArray(rawData?.data)
        ? rawData.data
        : Array.isArray(rawData)
        ? rawData
        : [];

      setPassengerDocumentRecords(documentRecords);
    };

    const onFailure = (err) => {
      dispatch(
        showSnackbar({
          message:
            err?.response?.data?.message ||
            "Failed to fetch passenger documents",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setPassengerDocumentRecords([]);
    };

    PassengerApi.getPassengerDocumentsByPassengerId(id).then(onSuccess, onFailure);
  }, [dispatch, id]);

  const getDetails = useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 200) {
        setDetails(res?.data?.data || {});
      } else {
        setDetails({});
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    PassengerApi.getPassengerDetails(id).then(onSuccess, onFailure);
  }, [dispatch, id]);

  useEffect(() => {
    if (openDialog && id) {
      setValue(0);
      setPassengerDocumentTypes(PASSENGER_DOCUMENT_DEFINITIONS);
      setPassengerDocumentRecords([]);
      setPassengerDocuments(
        buildEmptyPassengerDocuments(PASSENGER_DOCUMENT_DEFINITIONS)
      );
      getDetails();
      getDocumentTypes();
      getPassengerDocuments();
    }
  }, [openDialog, id, getDetails, getDocumentTypes, getPassengerDocuments]);

  /* eslint-disable react-hooks/exhaustive-deps */
  // Populate the form sections once passenger details are loaded.
  useEffect(() => {
    if (!openDialog || !id) {
      return;
    }

    const documentDefinitions =
      Array.isArray(passengerDocumentTypes) && passengerDocumentTypes.length > 0
        ? passengerDocumentTypes
        : PASSENGER_DOCUMENT_DEFINITIONS;

    if (!_.isEmpty(details)) {
      const passengerImage =
        resolveMediaUrl(details?.image_local || details?.image || "") ||
        defaultAvatar;

      setProfilePreview(passengerImage || defaultAvatar);

      basicFormik.setValues({
        firstName: details?.firstName || "",
        lastName: details?.lastName || "",
        email: details?.email || "",
        gender: details?.gender || "",
        country: details?.country || "",
        mobile: details?.mobileNumber || "",
        countryCode: details?.countryCode || "",
        customField1: details?.customField1 || "",
        customField2: details?.customField2 || "",
        passengerProfileImage: passengerImage || defaultAvatar,
        passengerProfileImagefile: passengerImage || defaultAvatar,
      });
    }

    setPassengerDocuments(
      normalizePassengerDocuments(
        details,
        documentDefinitions,
        passengerDocumentRecords
      )
    );
  }, [details, id, openDialog, passengerDocumentRecords, passengerDocumentTypes]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleCloseAndReset = () => {
    setValue(0);
    setSkipButton(false);
    setBackButton(false);
    setDetails({});
    setProfilePreview(defaultAvatar);
    setPassengerDocumentTypes(PASSENGER_DOCUMENT_DEFINITIONS);
    setPassengerDocumentRecords([]);
    passengerDocuments.forEach((document) => {
      if (isBlobUrl(document?.file)) {
        URL.revokeObjectURL(document.file);
      }
    });
    setPassengerDocuments(
      buildEmptyPassengerDocuments(PASSENGER_DOCUMENT_DEFINITIONS)
    );
    basicFormik.resetForm();
    handleClose();
  };

  const {
    getRootProps: getAvatarRootProps,
    getInputProps: getAvatarInputProps,
    open: openAvatar,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
    },
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (!file || file.size > maxFileSize) {
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          basicFormik.setFieldValue("passengerProfileImagefile", file);
          basicFormik.setFieldValue("passengerProfileImage", base64String);
          setProfilePreview(base64String);
        };
        reader.onerror = (error) => {
          console.error("Error converting image to Base64:", error);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleUpdatePassengerBasic = (formValues) => {
    if (!id) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));

    const data = {
      firstName: formValues?.firstName,
      lastName: formValues?.lastName,
      countryCode: formValues?.countryCode,
      mobileNumber: formValues?.mobile,
      email: formValues?.email,
      gender: formValues?.gender,
      country: formValues?.country,
      customField1: formValues?.customField1,
      customField2: formValues?.customField2,
    };

    if (formValues?.passengerProfileImagefile instanceof Blob) {
      data.image = formValues?.passengerProfileImagefile;
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 200) {
        dispatch(
          showSnackbar({
            message: "Passenger basic information updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        setValue(1);
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating passenger",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    PassengerApi.updatePassengers(id, { ...data }).then(onSuccess, onFailure);
  };

  const handleSubmitAndNext = () => {
    if (value === 0) {
      basicFormik.handleSubmit();
      return;
    }

    if (value === 1) {
      setValue(2);
      return;
    }

    if (value === 2) {
      handleCloseAndReset();
    }
  };

  const submitButtonLabel = value === 2 ? "Save & Close" : "Submit & Next";

  return (
    <>
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseAndReset}
        maxWidth={"xl"}
        aria-describedby="dialog-box"
        className={classes.root}
      >
        <DialogTitle>
          <section className={classes.dialogTitleContainer}>
            <Typography variant="body1" className={classes.title}>
              Update Passenger Details
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseAndReset}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </section>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={value} onChange={handleChange} aria-label="passenger update tabs">
                <Tab label="Basic Information" {...a11yProps(0)} />
                <Tab label="Documents" {...a11yProps(1)} />
                <Tab label="Status" {...a11yProps(2)} />
              </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
              <Grid container>
                <Grid item xs={12}>
                  <section className={classes.avatarContainer}>
                    <div className={classes.avatarWrapper}>
                      <div className={classes.avatar}>
                        <img
                          src={profilePreview || defaultAvatar}
                          alt="avatar"
                          className={classes.img}
                          style={{ objectFit: "cover" }}
                        />
                        <section
                          {...getAvatarRootProps()}
                          onClick={openAvatar}
                          className={classes.editIcon}
                        >
                          <input {...getAvatarInputProps()} />
                          <CameraAltOutlinedIcon />
                        </section>
                      </div>
                    </div>
                  </section>
                </Grid>
              </Grid>

              <Grid container>
                <section className={classes.container}>
                  <Grid item xs={12} className={classes.detailsContainer}>
                    <Grid container className={classes.containerWrapper}>
                      <Grid item xs={12} md={6} lg={6} xl={6}>
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              id="firstName"
                              name="firstName"
                              label="First Name"
                              value={basicFormik?.values?.firstName}
                              onChange={basicFormik.handleChange}
                              error={
                                basicFormik?.errors?.firstName &&
                                basicFormik?.touched?.firstName
                              }
                              helperText={
                                basicFormik?.errors?.firstName &&
                                basicFormik?.touched?.firstName &&
                                basicFormik?.errors?.firstName
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="lastName"
                              id="lastName"
                              label="Last Name"
                              value={basicFormik?.values?.lastName}
                              onChange={basicFormik.handleChange}
                              error={
                                basicFormik?.errors?.lastName &&
                                basicFormik?.touched?.lastName
                              }
                              helperText={
                                basicFormik?.errors?.lastName &&
                                basicFormik?.touched?.lastName &&
                                basicFormik?.errors?.lastName
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid item md={12} lg={12} xl={12}>
                            <Grid container>
                              <Grid
                                item
                                md={4}
                                lg={4}
                                xl={4}
                                className={classes.textFieldWrapper}
                              >
                                <TextField
                                  inputProps={{
                                    form: {
                                      autoComplete: "off",
                                    },
                                  }}
                                  name="countryCode"
                                  id="countryCode"
                                  label="Country Code"
                                  value={basicFormik?.values?.countryCode}
                                  onChange={basicFormik.handleChange}
                                  select
                                  error={
                                    basicFormik?.errors?.countryCode &&
                                    basicFormik?.touched?.countryCode
                                  }
                                  helperText={
                                    basicFormik?.errors?.countryCode &&
                                    basicFormik?.touched?.countryCode &&
                                    basicFormik?.errors?.countryCode
                                  }
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                >
                                  {countryCodeData.map((country) => (
                                    <MenuItem
                                      key={country.code}
                                      value={country.dial_code}
                                    >
                                      {`${country.code} (${country.dial_code})`}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid
                                item
                                md={8}
                                lg={8}
                                xl={8}
                                className={classes.textFieldWrapper}
                              >
                                <TextField
                                  inputProps={{
                                    form: {
                                      autoComplete: "off",
                                    },
                                  }}
                                  name="mobile"
                                  id="mobile"
                                  label="Mobile Number"
                                  value={basicFormik?.values?.mobile}
                                  onChange={basicFormik.handleChange}
                                  error={
                                    basicFormik?.errors?.mobile &&
                                    basicFormik?.touched?.mobile
                                  }
                                  helperText={
                                    basicFormik?.errors?.mobile &&
                                    basicFormik?.touched?.mobile &&
                                    basicFormik?.errors?.mobile
                                  }
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="email"
                              id="email"
                              label="Email"
                              value={basicFormik?.values?.email}
                              onChange={basicFormik.handleChange}
                              error={
                                basicFormik?.errors?.email &&
                                basicFormik?.touched?.email
                              }
                              helperText={
                                basicFormik?.errors?.email &&
                                basicFormik?.touched?.email &&
                                basicFormik?.errors?.email
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="gender"
                              id="gender"
                              select
                              label="Gender"
                              value={basicFormik?.values?.gender || ""}
                              onChange={basicFormik.handleChange}
                              error={
                                basicFormik?.errors?.gender &&
                                basicFormik?.touched?.gender
                              }
                              helperText={
                                basicFormik?.errors?.gender &&
                                basicFormik?.touched?.gender &&
                                basicFormik?.errors?.gender
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            >
                              {["male", "female"].map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option.charAt(0).toUpperCase() + option.slice(1)}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="country"
                              id="country"
                              select
                              label="Country"
                              value={basicFormik?.values?.country || ""}
                              onChange={basicFormik.handleChange}
                              error={
                                basicFormik?.errors?.country &&
                                basicFormik?.touched?.country
                              }
                              helperText={
                                basicFormik?.errors?.country &&
                                basicFormik?.touched?.country &&
                                basicFormik?.errors?.country
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            >
                              {countryCodeData.map((country) => (
                                <MenuItem key={country.code} value={country.name}>
                                  {`${country.code} (${country.name})`}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={6} lg={6} xl={6}>
                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          className={classes.textFieldWrapper}
                        >
                          <TextField
                            inputProps={{
                              form: {
                                autoComplete: "off",
                              },
                            }}
                            name="customField1"
                            id="customField1"
                            label="Custom Field 1"
                            value={basicFormik?.values?.customField1}
                            onChange={basicFormik.handleChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          className={classes.textFieldWrapper}
                        >
                          <TextField
                            inputProps={{
                              form: {
                                autoComplete: "off",
                              },
                            }}
                            name="customField2"
                            id="customField2"
                            label="Custom Field 2"
                            value={basicFormik?.values?.customField2}
                            onChange={basicFormik.handleChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </section>
              </Grid>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <PassengerDocuments
                passengerId={id}
                documents={passengerDocuments}
                setDocuments={setPassengerDocuments}
              />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={2}>
              <PassengerStatus
                passengerId={id}
                details={details}
                passengerDocuments={passengerDocuments}
                openDocumentsTab={handleOpenPassengerDocumentsTab}
                userType="USER"
              />
            </CustomTabPanel>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <section className={classes.dialogActions}>
            {backButton && (
              <section className={classes.skipBtn}>
                <Button onClick={handleBackButton}>Back</Button>
              </section>
            )}

            {skipButton && (
              <section className={classes.skipBtn}>
                <Button onClick={handleSkipButton}>Skip</Button>
              </section>
            )}

            <section className={classes.submBtn}>
              <Button onClick={handleSubmitAndNext}>{submitButtonLabel}</Button>
            </section>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PassengerUpdate;
